import {
  CloseOutlined,
  DownOutlined,
  PrinterOutlined,
  ProfileOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { App, Button, Dropdown, Table, Tooltip, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Key,
  type ThHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { api } from '@/api/client';
import { errorNotice } from '@/api/errorNotice';
import { fetchAllPages } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import type { ExportSourceColumn } from '@/lib/tableExport';

import { FileIcon } from './FileIcon';
import { useTableCard } from './TableCard';

/**
 * Таблица с закреплёнными заголовками и колонками, которые пользователь
 * переставляет и растягивает мышью.
 *
 * Отдельный компонент, а не пропсы на каждой странице: отступ сверху зависит
 * от высоты шапки конкретной карточки, а настройку колонок нужно хранить и
 * восстанавливать — повторять это в полутора десятках мест значит разъехаться
 * в одном из них.
 *
 * Перетаскивание и растягивание сделаны на штатных событиях браузера, без
 * внешней библиотеки: задача сводится к перестановке ячеек заголовка и смене
 * их ширины, и тянуть ради этого dnd-kit с react-resizable в сборку незачем.
 */

const STORAGE_PREFIX = 'tms.columns.';
/** Уже этого колонка не станет: заголовок должен оставаться читаемым. */
const MIN_WIDTH = 60;
/** Ширина колонки с галочками выбора. */
const SELECTION_WIDTH = 40;
/** Длительность исчезновения кнопки сброса колонок — как у tms-reset-out в styles.css. */
const RESET_LEAVE_MS = 700;
/** С такого числа строк отчёт собирается заметное время — показываем ход. */
const LARGE_REPORT_ROWS = 2000;
/**
 * Клик по этим элементам строку не выбирает: у кнопки, ссылки и поля ввода
 * своё действие, и галочка, вставшая заодно с ним, была бы неожиданностью.
 */
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], ' +
  '.ant-select, .ant-switch, .ant-image, .ant-table-row-expand-icon';

/**
 * Достаточно того, что есть у любой колонки. Конкретный ColumnType здесь не
 * нужен и только мешает: у группы колонок нет dataIndex.
 */
interface ColumnLike {
  key?: Key;
  dataIndex?: unknown;
  title?: unknown;
  width?: number | string;
  /**
   * Закреплённая колонка. Она не переставляется и остаётся на своём месте:
   * порядковый номер строки посреди таблицы смысла не имеет.
   */
  fixed?: unknown;
}

/** Ключ колонки: то, чем её можно опознать между сеансами. */
function columnKeyOf(column: ColumnLike, index: number): string {
  if (column.key !== undefined && column.key !== null) return String(column.key);
  if (column.dataIndex !== undefined && column.dataIndex !== null) {
    return String(column.dataIndex);
  }
  return `#${index}`;
}

/** Короткая свёртка строки — чтобы не хранить в ключе весь список колонок. */
function digest(input: string): string {
  let value = 0;
  for (let i = 0; i < input.length; i += 1) {
    value = (value * 31 + input.charCodeAt(i)) | 0;
  }
  return Math.abs(value).toString(36);
}

interface Layout {
  order: string[] | null;
  widths: Record<string, number>;
}

const EMPTY_LAYOUT: Layout = { order: null, widths: {} };

/**
 * Сохранённый порядок приводится к текущему набору колонок: исчезнувшие
 * отбрасываются, новые добавляются в конец. Иначе после доработки таблицы
 * пользователь со старой раскладкой не увидел бы новую колонку вовсе.
 */
function reconcile(saved: string[], keys: string[]): string[] {
  const known = new Set(keys);
  const kept = saved.filter((key) => known.has(key));
  const missing = keys.filter((key) => !kept.includes(key));
  return [...kept, ...missing];
}

function readLayout(storageId: string, keys: string[]): Layout {
  try {
    const raw = localStorage.getItem(storageId);
    if (!raw) return EMPTY_LAYOUT;
    const parsed: unknown = JSON.parse(raw);

    // Раскладки, сохранённые до появления ширины, хранились простым массивом.
    if (Array.isArray(parsed)) {
      return { order: reconcile(parsed as string[], keys), widths: {} };
    }

    const value = parsed as Partial<Layout>;
    return {
      order: Array.isArray(value.order) ? reconcile(value.order, keys) : null,
      widths: value.widths && typeof value.widths === 'object' ? value.widths : {},
    };
  } catch {
    // Испорченное значение в хранилище не должно ломать таблицу.
    return EMPTY_LAYOUT;
  }
}

interface HeaderApi {
  draggingKey: string | null;
  overKey: string | null;
  begin: (key: string) => void;
  hover: (key: string) => void;
  drop: (key: string) => void;
  end: () => void;
  startResize: (key: string, event: React.MouseEvent<HTMLElement>) => void;
  resizingKey: string | null;
}

const HeaderContext = createContext<HeaderApi | null>(null);

/**
 * Ячейка заголовка: ручка перетаскивания и ручка изменения ширины.
 * Ключ колонки приходит через data-атрибут — он проходит в DOM без
 * предупреждений React, в отличие от произвольного пропса.
 */
function HeaderCell(
  props: ThHTMLAttributes<HTMLTableCellElement> & { 'data-col-key'?: string },
) {
  const api = useContext(HeaderContext);
  const columnKey = props['data-col-key'];

  // Колонки без заголовка (кнопки действий) не настраиваются: ухватиться
  // там не за что, и место у правого края для них единственно верное.
  if (!api || !columnKey) {
    return <th {...props} />;
  }

  const isOver = api.overKey === columnKey && api.draggingKey !== columnKey;
  const isResizing = api.resizingKey === columnKey;
  const className = [props.className, 'tms-th-interactive', isOver ? 'tms-th-over' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <th
      {...props}
      className={className}
      // Пока тянут ширину, перетаскивание колонки должно быть выключено:
      // иначе браузер начнёт перенос вместо изменения размера.
      draggable={!api.resizingKey}
      onDragStart={(event) => {
        // Без данных в dataTransfer Firefox не начинает перетаскивание.
        event.dataTransfer.setData('text/plain', columnKey);
        event.dataTransfer.effectAllowed = 'move';
        api.begin(columnKey);
      }}
      onDragOver={(event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        api.hover(columnKey);
      }}
      onDrop={(event) => {
        event.preventDefault();
        api.drop(columnKey);
      }}
      onDragEnd={api.end}
    >
      {props.children}
      <span
        className={`tms-th-resizer${isResizing ? ' tms-th-resizer--active' : ''}`}
        role="presentation"
        onMouseDown={(event) => {
          // Иначе начнётся перетаскивание колонки, а не изменение ширины.
          event.stopPropagation();
          event.preventDefault();
          api.startResize(columnKey, event);
        }}
        onClick={(event) => event.stopPropagation()}
      />
    </th>
  );
}

/**
 * Колонка таблицы платформы: обычная колонка antd плюс настройки отчёта.
 */
export type StickyColumn<RecordType> = NonNullable<TableProps<RecordType>['columns']>[number] & {
  /**
   * false — в отчёте по этой колонке не считается «Итого». Нужен числовым
   * колонкам, которые складывать бессмысленно: показания одометра, цена за
   * единицу, расход на 100 км, остатки в разных единицах измерения.
   */
  exportTotal?: boolean;
};

export interface StickyTableProps<RecordType> extends Omit<TableProps<RecordType>, 'columns'> {
  columns?: Array<StickyColumn<RecordType>>;
  /**
   * Идентификатор раскладки. Нужен только там, где на одном экране
   * несколько таблиц со схожим набором колонок; иначе вычисляется сам.
   */
  columnsKey?: string;
  /** Колонка с порядковым номером строки, закреплённая слева. */
  rowNumbers?: boolean;
  /**
   * Выбор строк галочками и кнопка «Отчёт» по выбранному. Включён
   * по умолчанию: правило общее для всех списков платформы, и отказ от него
   * (как у журнала действий) должен быть виден на месте вызова.
   */
  selectable?: boolean;
  /** Название списка для выгрузки; по умолчанию — заголовок карточки. */
  exportName?: string;
  /**
   * Откуда взять все строки постраничного списка для отчёта «весь список»:
   * адрес и те же фильтры, с которыми загружена страница на экране. Без него
   * у постраничной таблицы доступен только отчёт по выбранным строкам —
   * у таблицы есть лишь текущая страница.
   */
  exportSource?: { url: string; params?: Record<string, unknown> };
  /**
   * Собственные виды отчётов этого списка — печатные формы документов пачкой,
   * а со временем и формы отчётности конкретного офиса. Встают в меню «Отчёт»
   * после сводной таблицы.
   */
  reports?: (rows: RecordType[]) => ReportDefinition[];
}

/**
 * Вид отчёта в меню «Отчёт»: строка с названием и значками форматов, в
 * которых он доступен. У каждого вида набор форматов свой — один выгружается
 * только в PDF, другой в XLS, DOC и PDF.
 */
export interface ReportDefinition {
  key: string;
  label: string;
  formats: ReportFormat[];
  /** Вид отчёта сейчас недоступен; hint объясняет почему. */
  disabled?: boolean;
  hint?: string;
}

export interface ReportFormat {
  /**
   * Имя значка из библиотеки icons/: xls, pdf, doc, csv… Особое значение
   * print — отчёт не скачивается файлом, а открывается для печати.
   */
  type: string;
  /** Подсказка при наведении; по умолчанию — тип заглавными: «XLS». */
  title?: string;
  run: () => void | Promise<void>;
}

/**
 * Колонка нумерации.
 *
 * Смещение берётся из настроек постраничного вывода: Ant Design передаёт в
 * отрисовку номер строки внутри текущей выборки, поэтому без поправки на
 * страницу отсчёт начинался бы заново на каждой из них.
 */
function rowNumberColumn(offset: number) {
  return {
    title: '№',
    key: 'rowNumber',
    width: 44,
    align: 'center' as const,
    fixed: 'left' as const,
    render: (_: unknown, __: unknown, index: number) => (
      <Typography.Text type="secondary">{offset + index + 1}</Typography.Text>
    ),
  };
}

export function StickyTable<RecordType extends object>({
  columnsKey,
  rowNumbers,
  selectable = true,
  exportName,
  exportSource,
  reports,
  columns,
  components,
  ...props
}: StickyTableProps<RecordType>) {
  const { offsetHeader, selectionSlot, title: cardTitle } = useTableCard();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { message } = App.useApp();

  /*
    Ключи считаются один раз по ИСХОДНОМУ порядку и дальше носятся вместе с
    колонкой. У колонки без key и dataIndex ключ выводится из позиции, а после
    перестановки позиция другая — пересчёт на переставленном списке дал бы
    другие ключи, и сопоставление рассыпалось бы.
  */
  // Смещение нумерации: у таблицы без постраничного вывода оно нулевое.
  const pageOffset =
    props.pagination && typeof props.pagination === 'object'
      ? ((props.pagination.current ?? 1) - 1) * (props.pagination.pageSize ?? 10)
      : 0;

  const allColumns = useMemo(
    () => (rowNumbers && columns ? [rowNumberColumn(pageOffset), ...columns] : columns),
    [rowNumbers, columns, pageOffset],
  );

  const entries = useMemo(
    () =>
      (allColumns ?? []).map((column, index) => ({
        key: columnKeyOf(column as ColumnLike, index),
        column,
        pinned: Boolean((column as ColumnLike).fixed),
      })),
    [allColumns],
  );
  const keys = useMemo(() => entries.map((entry) => entry.key), [entries]);
  // Переставляются только незакреплённые: закреплённая колонка в перестановке
  // не участвует ни как груз, ни как место назначения.
  const movableKeys = useMemo(
    () => entries.filter((entry) => !entry.pinned).map((entry) => entry.key),
    [entries],
  );
  const signature = keys.join('|');
  const movableSignature = movableKeys.join('|');
  const storageId = `${STORAGE_PREFIX}${columnsKey ?? `${pathname}:${digest(signature)}`}`;

  const [layout, setLayout] = useState<Layout>(EMPTY_LAYOUT);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);
  const [resizingKey, setResizingKey] = useState<string | null>(null);
  const draggingRef = useRef<string | null>(null);
  const layoutRef = useRef<Layout>(EMPTY_LAYOUT);

  layoutRef.current = layout;

  useEffect(() => {
    setLayout(readLayout(storageId, movableSignature.split('|')));
  }, [storageId, movableSignature]);

  /*
    Подсказка с текстом ячейки — на любой ячейке, чтобы длинное значение
    можно было прочесть, даже когда колонка сужена. Одна на всю таблицу и по
    делегированию событий: на каждую ячейку отдельный Tooltip означал бы
    сотни компонентов на странице. Появляется с небольшой задержкой, чтобы не
    мелькать, пока курсор просто пересекает таблицу.
  */
  const [cellTip, setCellTip] = useState<{
    cell: HTMLElement;
    text: string;
    rect: DOMRect;
  } | null>(null);
  const lastCellTip = useRef<typeof cellTip>(null);
  const tipTimer = useRef<number | undefined>(undefined);
  const tipTarget = useRef<HTMLElement | null>(null);

  const hideCellTip = useCallback(() => {
    window.clearTimeout(tipTimer.current);
    tipTarget.current = null;
    setCellTip(null);
  }, []);

  useEffect(() => () => window.clearTimeout(tipTimer.current), []);

  const showCellTip = (event: React.MouseEvent<HTMLElement>): void => {
    const cell = (event.target as HTMLElement).closest<HTMLElement>('td.ant-table-cell');
    if (!cell) {
      hideCellTip();
      return;
    }
    if (tipTarget.current === cell) return;

    const text = cell.innerText.replace(/\s*\n\s*/g, ', ').trim();
    if (!text) {
      hideCellTip();
      return;
    }

    window.clearTimeout(tipTimer.current);
    tipTarget.current = cell;
    const reveal = () => setCellTip({ cell, text, rect: cell.getBoundingClientRect() });
    // Уже открытая подсказка перескакивает на соседнюю ячейку сразу.
    if (cellTip) reveal();
    else tipTimer.current = window.setTimeout(reveal, 350);
  };

  // Прокрутка сдвигает ячейку из-под подсказки — проще её убрать.
  useEffect(() => {
    if (!cellTip) return;
    window.addEventListener('scroll', hideCellTip, true);
    return () => window.removeEventListener('scroll', hideCellTip, true);
  }, [cellTip, hideCellTip]);

  // ─── Выбор строк ─────────────────────────────────────────────────────────
  // Страница, задавшая собственный rowSelection, управляет выбором сама.
  const ownSelection = selectable && !props.rowSelection;

  /*
    Хранятся и ключи, и сами строки: списки постраничные, и строка, отмеченная
    на первой странице, после перехода на вторую из dataSource исчезает —
    выгружать её было бы не из чего.
  */
  const [selection, setSelection] = useState<{ keys: Key[]; rows: RecordType[] }>({
    keys: [],
    rows: [],
  });
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [reportOpen, setReportOpen] = useState(false);

  const { rowKey } = props;
  const keyOf = (row: RecordType): Key =>
    typeof rowKey === 'function'
      ? rowKey(row)
      : (row as Record<string, Key>)[typeof rowKey === 'string' ? rowKey : 'key'];

  const toggleRow = (row: RecordType): void => {
    const key = keyOf(row);
    setSelection((current) =>
      current.keys.includes(key)
        ? {
            keys: current.keys.filter((item) => item !== key),
            rows: current.rows.filter((item) => keyOf(item) !== key),
          }
        : { keys: [...current.keys, key], rows: [...current.rows, row] },
    );
  };

  /*
    Вкладки держат свои таблицы смонтированными, просто пряча неактивные.
    Панель выбора у скрытой таблицы показывать нельзя: в общей шапке карточки
    оказались бы две панели сразу. Скрытый блок имеет нулевую ширину — по ней
    и определяем.
  */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(true);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver(([entry]) => setOnScreen(entry.contentRect.width > 0));
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  const persist = (next: Layout): void => {
    setLayout(next);
    try {
      localStorage.setItem(storageId, JSON.stringify(next));
    } catch {
      // Переполненное или недоступное хранилище — не повод ломать настройку.
    }
  };

  const effectiveOrder = layout.order ?? movableKeys;

  /*
    Закреплённые колонки остаются на своих исходных местах, а переставляемые
    занимают оставшиеся позиции в выбранном пользователем порядке. Так номер
    строки не уедет в середину таблицы, даже если в хранилище лежит раскладка,
    сохранённая до его закрепления.
  */
  const orderedEntries = useMemo(() => {
    const movableByKey = new Map(
      entries.filter((entry) => !entry.pinned).map((entry) => [entry.key, entry]),
    );
    const queue = effectiveOrder
      .map((key) => movableByKey.get(key))
      .filter((entry): entry is (typeof entries)[number] => entry !== undefined);

    let next = 0;
    return entries.map((entry) => (entry.pinned ? entry : (queue[next++] ?? entry)));
  }, [entries, effectiveOrder]);

  const isCustomised =
    (layout.order !== null && layout.order.join('|') !== movableSignature) ||
    Object.keys(layout.widths).length > 0;

  // Стабильная ссылка: функция уходит в зависимости эффекта ниже.
  const reset = useCallback((): void => {
    setLayout(EMPTY_LAYOUT);
    try {
      localStorage.removeItem(storageId);
    } catch {
      // См. выше: недоступное хранилище не должно ломать сброс на экране.
    }
  }, [storageId]);

  const headerApi: HeaderApi = {
    draggingKey,
    overKey,
    resizingKey,
    begin: (key) => {
      draggingRef.current = key;
      setDraggingKey(key);
    },
    hover: (key) => setOverKey(key),
    drop: (target) => {
      const source = draggingRef.current;
      draggingRef.current = null;
      setDraggingKey(null);
      setOverKey(null);
      if (!source || source === target) return;

      const next = [...effectiveOrder];
      const from = next.indexOf(source);
      const to = next.indexOf(target);
      if (from < 0 || to < 0) return;
      next.splice(from, 1);
      next.splice(to, 0, source);
      persist({ ...layoutRef.current, order: next });
    },
    end: () => {
      draggingRef.current = null;
      setDraggingKey(null);
      setOverKey(null);
    },
    startResize: (key, event) => {
      const cell = (event.target as HTMLElement).closest('th');
      if (!cell) return;

      const startX = event.clientX;
      const startWidth = cell.getBoundingClientRect().width;

      // Фиксируем текущую ширину ВСЕХ колонок: иначе колонки без заданной ширины
      // растягиваются и «съедают» освободившееся место, а суженная колонка
      // визуально остаётся прежней.
      const measured: Record<string, number> = {};
      cell.parentElement
        ?.querySelectorAll<HTMLElement>('th[data-col-key]')
        .forEach((th) => {
          const columnKey = th.dataset.colKey;
          if (columnKey)
            measured[columnKey] = Math.round(th.getBoundingClientRect().width);
        });
      setLayout((current) => ({
        ...current,
        widths: { ...measured, ...current.widths },
      }));
      layoutRef.current = {
        ...layoutRef.current,
        widths: { ...measured, ...layoutRef.current.widths },
      };
      setResizingKey(key);

      // Слушатели на документе, а не на ячейке: курсор при быстром движении
      // выходит за её пределы, и события ушли бы мимо.
      const onMove = (move: MouseEvent): void => {
        const width = Math.max(MIN_WIDTH, Math.round(startWidth + move.clientX - startX));
        setLayout((current) => ({
          ...current,
          widths: { ...current.widths, [key]: width },
        }));
      };

      const onUp = (): void => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.classList.remove('tms-resizing');
        setResizingKey(null);
        // В хранилище пишем один раз по отпусканию, а не на каждый пиксель.
        persist(layoutRef.current);
      };

      // Курсор изменения размера на всё время перетаскивания, где бы он ни был.
      document.body.classList.add('tms-resizing');
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
  };

  const preparedColumns = allColumns
    ? orderedEntries.map(({ key, column, pinned }) => {
        const typed = column as ColumnLike;
        const width = layout.widths[key] ?? typed.width;

        // Без заголовка не за что ухватиться, закреплённую двигать нельзя —
        // и в том, и в другом случае ячейка остаётся обычной.
        if (pinned || !typed.title) {
          return width === typed.width ? column : { ...column, width };
        }

        return {
          ...column,
          width,
          onHeaderCell: () => ({ 'data-col-key': key }) as never,
        };
      })
    : allColumns;

  /*
    Когда ширины заданы пользователем, таблица получает ровно суммарную ширину
    колонок и не растягивается до краёв: суженная колонка остаётся суженной,
    соседние сдвигаются влево. Работает, только если у каждой колонки известна
    числовая ширина; иначе остаётся обычное поведение.
  */
  const fixedTotal = (() => {
    if (Object.keys(layout.widths).length === 0) return null;
    // Колонка галочек в списке колонок не числится, но место занимает.
    let total = ownSelection ? SELECTION_WIDTH : 0;
    for (const { key, column } of orderedEntries) {
      const width = layout.widths[key] ?? (column as ColumnLike).width;
      if (typeof width !== 'number') return null;
      total += width;
    }
    return total;
  })();

  /*
    Кнопка сброса настройки колонок — в строке с номерами страниц, слева от
    «Всего: N», и только пока колонки настроены. Действие редкое, поэтому ему
    не место ни в шапке рядом с фильтрами, ни отдельной строкой под таблицей:
    в строке страниц оно на виду ровно настолько, чтобы его нашли, и ничего
    не сдвигает.
  */
  /*
    Кнопка исчезает так же плавно, как появляется. Для этого после сброса она
    ещё недолго остаётся в разметке — в состоянии «уходит», пока идёт анимация
    (см. tms-reset-out в styles.css), — и только потом убирается совсем.
    Убранная сразу, она рывком возвращала бы «Всего: N» на прежнее место.
  */
  const [resetPhase, setResetPhase] = useState<'hidden' | 'shown' | 'leaving'>('hidden');

  useEffect(() => {
    setResetPhase((phase) => {
      if (isCustomised) return 'shown';
      return phase === 'shown' ? 'leaving' : phase;
    });
  }, [isCustomised]);

  useEffect(() => {
    if (resetPhase !== 'leaving') return;
    const timer = window.setTimeout(() => setResetPhase('hidden'), RESET_LEAVE_MS);
    return () => window.clearTimeout(timer);
  }, [resetPhase]);

  const resetButton =
    resetPhase === 'hidden' ? null : (
      <Tooltip
        title={resetPhase === 'shown' ? t('Вернуть исходный порядок и ширину колонок') : ''}
      >
        <button
          type="button"
          className={`tms-columns-reset${resetPhase === 'leaving' ? ' tms-columns-reset--leaving' : ''}`}
          // Уходящая кнопка уже отработала: повторное нажатие ничего не должно делать.
          disabled={resetPhase === 'leaving'}
          onClick={reset}
        >
          <UndoOutlined />
          <span>{t('Сбросить колонки')}</span>
        </button>
      </Tooltip>
    );

  // Строка страниц есть не всегда: её нет у таблицы без постраничного вывода,
  // у пустой таблицы и там, где единственная страница скрывает её сама.
  const pager = props.pagination === false ? null : (props.pagination ?? {});
  const pagerRows = pager ? (pager.total ?? props.dataSource?.length ?? 0) : 0;
  const pagerShown =
    pager !== null &&
    pagerRows > 0 &&
    !(pager.hideOnSinglePage && pagerRows <= (pager.pageSize ?? 10));

  const totalRows =
    props.pagination && typeof props.pagination === 'object' && props.pagination.total !== undefined
      ? props.pagination.total
      : (props.dataSource?.length ?? 0);

  // Постраничный список: у таблицы на руках только текущая страница.
  const serverPaged = totalRows > (props.dataSource?.length ?? 0);
  const canExportAll = !serverPaged || Boolean(exportSource);

  /** Ключ сообщения о ходе отчёта: одно и то же сообщение обновляется на месте. */
  const progressKey = `tms-report-${storageId}`;
  const showProgress = (content: string): void => {
    void message.open({ key: progressKey, type: 'loading', content, duration: 0 });
  };

  const loadAllRows = async (): Promise<RecordType[]> => {
    if (!serverPaged || !exportSource) return [...(props.dataSource ?? [])];

    return fetchAllPages<RecordType>(exportSource.url, exportSource.params ?? {}, (loaded, total) => {
      // Единственная пачка приходит мгновенно — сообщение только мелькнуло бы.
      if (total > loaded || loaded > 2000) {
        showProgress(`${t('Загрузка строк')}: ${loaded} ${t('из')} ${total}`);
      }
    });
  };

  /*
    Выгружается то, что на экране: колонки в порядке и ширине, настроенных
    пользователем. Строки — либо отмеченные галочками, либо весь список под
    текущим фильтром. Модуль выгрузки подгружается по нажатию: библиотеки
    Excel и PDF в основной сборке не нужны.
  */
  const runExport = async (format: 'xlsx' | 'pdf', scope: 'selected' | 'all'): Promise<void> => {
    setExporting(format);
    try {
      const rows = scope === 'all' ? await loadAllRows() : selection.rows;
      if (rows.length === 0) {
        void message.info(t('Нет строк для отчёта'));
        return;
      }

      // Большой отчёт собирается заметное время, и вкладка на это время
      // замирает: сообщение должно успеть появиться до начала сборки.
      if (rows.length > LARGE_REPORT_ROWS) {
        showProgress(`${t('Формируется отчёт')}: ${rows.length}`);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      const { buildExportTable, downloadXlsx, downloadPdf } = await import('@/lib/tableExport');

      const name = exportName ?? cardTitle ?? t('Таблица');
      const stamp = dayjs().format('DD.MM.YYYY HH:mm');
      const table = buildExportTable<RecordType>(
        name,
        [user?.activeOffice.name, `${t('Строк')}: ${rows.length}`].filter(Boolean).join(' · '),
        orderedEntries
          .filter((entry) => entry.key !== 'rowNumber')
          .map(({ key, column }) => ({
            ...(column as ExportSourceColumn<RecordType>),
            width: layout.widths[key] ?? (column as ColumnLike).width,
          })),
        rows,
        `${t('Итого')} (${t('Строк').toLowerCase()}: ${rows.length})`,
      );

      if (format === 'xlsx') await downloadXlsx(table);
      else await downloadPdf(table, `${t('Сформировано')}: ${stamp} · ${user?.fullName ?? ''}`);

      // След в журнале действий: кто, из какого списка и сколько строк вынес.
      // Сбой записи выгрузку не отменяет — файл уже у пользователя.
      void api
        .post('/audit/export', { table: name, path: pathname, format, rows: rows.length })
        .catch(() => undefined);
    } catch (error) {
      void message.error(errorNotice(error));
    } finally {
      message.destroy(progressKey);
      setExporting(null);
    }
  };

  /*
    Кнопка «Отчёт» стоит в шапке постоянно и нажимается всегда: отчёт по всему
    списку галочек не требует. Постоянная, а не появляющаяся с первой
    галочкой: возникая в шапке, она сдвигала фильтры и саму таблицу.

    В меню — виды отчётов, по строке на каждый; справа в строке значки
    форматов, в которых этот вид доступен. Видов будет становиться больше
    (у офиса может быть своя форма отчётности), и набор форматов у каждого
    свой, поэтому формат выбирается значком в строке, а не отдельным пунктом.
    Вид, которому нужны отмеченные строки, без них остаётся в меню неактивным:
    так видно, что он есть и что для него нужно сделать.
  */
  const selectedCount = selection.keys.length;
  const needSelection = selectedCount === 0;
  const selectionHint = t('Отметьте строки галочками');

  const reportDefinitions: ReportDefinition[] = [
    {
      key: 'selected',
      label: `${t('Сводная таблица')} — ${t('выбранные')} (${selectedCount})`,
      disabled: needSelection,
      hint: selectionHint,
      formats: [
        { type: 'xls', run: () => runExport('xlsx', 'selected') },
        { type: 'pdf', run: () => runExport('pdf', 'selected') },
      ],
    },
    ...(canExportAll
      ? [
          {
            key: 'all',
            label: `${t('Сводная таблица')} — ${t('весь список')} (${totalRows})`,
            disabled: totalRows === 0,
            formats: [
              { type: 'xls', run: () => runExport('xlsx', 'all') },
              { type: 'pdf', run: () => runExport('pdf', 'all') },
            ],
          },
        ]
      : []),
    // Отчёты страницы строятся по отмеченным строкам.
    ...(reports?.(selection.rows) ?? []).map((definition) => ({
      ...definition,
      disabled: definition.disabled || needSelection,
      hint: definition.hint ?? selectionHint,
    })),
  ];

  const reportPanel = (
    <div className="tms-report-panel">
      <div className="tms-report-head">
        {t('Выбрано')}: {selectedCount} {t('из')} {totalRows}
      </div>
      {reportDefinitions.map((definition) => (
        <div
          key={definition.key}
          className={`tms-report-row${definition.disabled ? ' tms-report-row--disabled' : ''}`}
        >
          <span className="tms-report-name">{definition.label}</span>
          <span className="tms-report-formats">
            {definition.formats.map((format) => {
              const formatTitle = format.title ?? format.type.toUpperCase();
              return (
                <Tooltip
                  key={format.type}
                  title={definition.disabled ? (definition.hint ?? '') : formatTitle}
                >
                  {/* Обёртка нужна подсказке: неактивная кнопка событий мыши не отдаёт. */}
                  <span>
                    <button
                      type="button"
                      className="tms-report-format"
                      aria-label={`${definition.label} — ${formatTitle}`}
                      disabled={definition.disabled}
                      onClick={() => {
                        setReportOpen(false);
                        void format.run();
                      }}
                    >
                      {format.type === 'print' ? (
                        <PrinterOutlined style={{ fontSize: 16 }} />
                      ) : (
                        <FileIcon type={format.type} size={20} />
                      )}
                    </button>
                  </span>
                </Tooltip>
              );
            })}
          </span>
        </div>
      ))}
      {selectedCount > 0 && (
        <button
          type="button"
          className="tms-report-clear"
          onClick={() => {
            setReportOpen(false);
            setSelection({ keys: [], rows: [] });
          }}
        >
          <CloseOutlined /> {t('Снять выбор')}
        </button>
      )}
    </div>
  );

  const selectionBar =
    ownSelection && onScreen ? (
      <Dropdown
        trigger={['click']}
        open={reportOpen}
        onOpenChange={setReportOpen}
        popupRender={() => reportPanel}
      >
        <Button icon={<ProfileOutlined />} loading={exporting !== null}>
          {t('Отчёт')}
          {selectedCount > 0 && ` (${selectedCount})`}
          <DownOutlined />
        </Button>
      </Dropdown>
    ) : null;

  // Последняя показанная подсказка остаётся в разметке, пока идёт плавное
  // исчезновение: без содержимого antd убрал бы её мгновенно.
  if (cellTip) lastCellTip.current = cellTip;
  const shownTip = cellTip ?? lastCellTip.current;

  return (
    <HeaderContext.Provider value={headerApi}>
      <div ref={wrapperRef} onMouseOver={showCellTip} onMouseLeave={hideCellTip}>
        {/* В шапку карточки — порталом; у таблицы вне карточки шапки нет,
            и кнопка встаёт прямо над ней. */}
        {selectionBar &&
          (selectionSlot ? (
            createPortal(selectionBar, selectionSlot)
          ) : (
            <div className="tms-selection-inline">{selectionBar}</div>
          ))}
        <Tooltip
          open={cellTip !== null}
          title={
            shownTip && (
              <span
                key={`${shownTip.rect.left}:${shownTip.rect.top}`}
                className="tms-cell-tip-text"
              >
                {shownTip.text}
              </span>
            )
          }
          placement="bottom"
          rootClassName="tms-cell-tip"
          styles={{ root: { maxWidth: 480, pointerEvents: 'none' } }}
        >
          <span
            aria-hidden
            style={{
              position: 'fixed',
              pointerEvents: 'none',
              left: shownTip?.rect.left ?? 0,
              top: shownTip?.rect.top ?? 0,
              width: shownTip?.rect.width ?? 0,
              height: shownTip?.rect.height ?? 0,
            }}
          />
        </Tooltip>
        <Table<RecordType>
          sticky={{ offsetHeader }}
          /*
          Компактные строки. По умолчанию Ant Design отводит на ячейку 16
          пикселей сверху и снизу — для учётной системы, где на экран нужно
          уместить как можно больше строк, это расточительно. Размер шрифта
          при этом не меняется, читаемость прежняя.
          Задано до распространения props: страница может вернуть себе
          просторный вариант, передав size явно.
        */
          size="small"
          rowSelection={
            ownSelection
              ? {
                  fixed: true,
                  columnWidth: SELECTION_WIDTH,
                  // Отмеченное на одной странице не теряется при переходе на другую.
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selection.keys,
                  onChange: (keys, rows) => setSelection({ keys, rows }),
                }
              : undefined
          }
          /*
          Клик по строке выбирает её — но только там, где у строки нет своего
          действия. Где клик открывает карточку (транспорт, водители, путевые
          листы), страница передаёт свой onRow, и он перекрывает этот.
        */
          onRow={
            ownSelection
              ? (row) => ({
                  onClick: (event) => {
                    const target = event.target as HTMLElement;
                    // Клик внутри всплывающего окна (подтверждение удаления)
                    // всплывает сюда по дереву React, хотя в строке не был.
                    if (!event.currentTarget.contains(target)) return;
                    if (target.closest(INTERACTIVE_SELECTOR)) return;
                    // Выделение текста мышью для копирования — не выбор строки.
                    if (window.getSelection()?.toString()) return;
                    toggleRow(row);
                  },
                })
              : undefined
          }
          {...props}
          /*
          Широкая таблица прокручивается внутри себя, а не растягивает страницу.
          Без этого страница уезжала вбок, и первые колонки — гаражный номер и
          госномер — оказывались под боковым меню. Страница может задать свою
          прокрутку; заданная пользователем ширина колонок главнее.
        */
          scroll={
            fixedTotal !== null
              ? { ...props.scroll, x: fixedTotal }
              : (props.scroll ?? { x: 'max-content' })
          }
          className={[
            'tms-sticky-table',
            fixedTotal !== null && 'tms-table-fixed-width',
            props.className,
          ]
            .filter(Boolean)
            .join(' ')}
          columns={preparedColumns}
          components={{
            ...components,
            header: { ...components?.header, cell: HeaderCell },
          }}
          // После {...props}: кнопка сброса встаёт в строку страниц перед
          // подписью «Всего: N», которую задаёт страница.
          pagination={
            pager && pagerShown && resetButton
              ? {
                  ...pager,
                  showTotal: (total, range) => (
                    <span className="tms-pager-total">
                      {resetButton}
                      {pager.showTotal?.(total, range)}
                    </span>
                  ),
                }
              : props.pagination
          }
        />
        {/* Строки страниц нет — кнопка встаёт под таблицей, справа. */}
        {resetButton && !pagerShown && (
          <div
            className={`tms-columns-reset-row${
              resetPhase === 'leaving' ? ' tms-columns-reset-row--leaving' : ''
            }`}
          >
            {resetButton}
          </div>
        )}
      </div>
    </HeaderContext.Provider>
  );
}
